import React, { useState } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import Axios from '@/utils/axios';
import { customerPrefixes } from '@/constants/base';

import Input from '../../Input/Input';
import Textarea from '../../Textarea/Textarea';
import Button from '../../Button/Button';
import CheckboxWithTitle from '../../CheckboxWithTitle/CheckboxWithTitle';
import ScreenLoader from '../../ScreenLoader/ScreenLoader';
import InputWithSelect from '../../InputWithSelect/InputWithSelect';

import styles from './ContactForm.module.scss';

const employees = [
  {
    id: 3,
    title: <Trans i18nKey={'common:Up to'} />,
    value: 'up_to_10',
  },
  {
    id: 4,
    title: <Trans i18nKey={'common:From to'} />,
    value: 'from_10',
  },
];

const ContactForm = ({
  tariffID = null,
  showSuccess = () => null,
  employeeID = '',
  hasEmployeesCount = true,
}) => {
  const { asPath, route } = useRouter();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const isBusinessPage = route.startsWith('/business');
  const FormSchema = Yup.object().shape({
    fullName: Yup.string().min(1).max(150).required(t('common:Required')),
    phone: Yup.string().required(t('common:Required')),
    ...(isBusinessPage && {
      company: Yup.string().min(1).max(150).required(t('common:Required')),
    }),
    email: Yup.string()
      .email(t('common:Invalid email'))
      .required(t('common:Invalid email')),
    comment: Yup.string(),
    agreement: Yup.boolean().oneOf([true], t('common:Required')),
  });
  const formik = useFormik({
    initialValues: {
      fullName: '',
      phone: '',
      phoneNumberCode: customerPrefixes[0],
      ...(isBusinessPage && {
        company: '',
      }),
      employees: employeeID || '',
      email: '',
      comment: '',
      tariff_id: '',
      agreement: true,
      path: asPath,
      employees_count: '',
      ...(tariffID !== null ? { tariffID } : {}),
    },
    validationSchema: FormSchema,
    onSubmit: async (values) => {
      const data = {
        fullname: values?.fullName,
        phone: `${values?.phoneNumberCode?.name}${values?.phone}`,
        title_of_company: values?.company || '',
        email: values?.email,
        comment: values?.comment,
        employees_count: values?.employeesCount,
      };
      try {
        setLoading(true);
        const response = await Axios.post('subscription-request', data);
        if (response) {
          showSuccess();
        }
      } catch (error) {
        // console.log(error)
      } finally {
        setLoading(false);
        setTimeout(formik.resetForm, 300);
      }
    },
  });

  const onClickHandler = (id, value) => {
    formik.setFieldValue('employees', id);
    formik.setFieldValue('employeesCount', value);
  };
  const renderEmployees = ({ title = '', value = '', id = '' }, key) => {
    return (
      <li
        key={key}
        className={formik.values?.employees === id ? styles.active : ''}
        onClick={() => onClickHandler(id, value)}
        itemScope
        itemType='https://schema.org/Person'
      >
        {title}
      </li>
    );
  };

  return (
    <div itemScope itemType='https://schema.org/Action'>
      <ScreenLoader visible={loading} />
      <div className={'title'}>{t('common:Contact')}</div>
      <div className={'note'}>{t('common:Contact description')}</div>
      <div className={styles.content}>
        <Input
          onBlur={formik.handleBlur('fullName')}
          value={formik.values.fullName}
          onChange={(value) => formik.setFieldValue('fullName', value)}
          placeholder={t('common:Fullname placeholder')}
          label={t('common:Fullname')}
          error={formik.touched.fullName && formik.errors.fullName}
        />
        <InputWithSelect
          onBlur={formik.handleBlur('phone')}
          error={formik.touched.phone && formik.errors.phone}
          selected={formik.values.phoneNumberCode.name}
          label={t('common:Phone')}
          placeholder={'XXX-XX-XX'}
          mask={'999 99 99'}
          options={customerPrefixes.slice(0, 2)}
          value={formik.values.phoneNumber}
          onChange={(value) => formik.setFieldValue('phone', value)}
          onSelect={(value) => formik.setFieldValue('phoneNumberCode', value)}
        />
        {isBusinessPage && (
          <Input
            onBlur={formik.handleBlur('company')}
            value={formik.values.company}
            onChange={(value) => formik.setFieldValue('company', value)}
            placeholder={t('common:Company placeholder')}
            label={t('common:Company')}
            error={formik.touched.company && formik.errors.company}
          />
        )}
        {!!employees.length && hasEmployeesCount && (
          <div className={styles.employees}>
            <span>{t('common:Employee count')}</span>
            <ul>{employees.map(renderEmployees)}</ul>
            {formik.touched.employees && (
              <div className={styles.error}>{formik.errors.employees}</div>
            )}
          </div>
        )}
        <Input
          onBlur={formik.handleBlur('email')}
          value={formik.values.email}
          onChange={(value) => formik.setFieldValue('email', value)}
          error={formik.touched.email && formik.errors.email}
          placeholder={t('common:Email placeholder')}
          type={'email'}
          label={t('common:Email')}
        />
        <div className={styles.textarea}>
          <Textarea
            value={formik.values.comment}
            error={formik.touched.comment && formik.errors.comment}
            onChange={(value) => formik.setFieldValue('comment', value)}
            placeholder={t('common:Addition placeholder')}
            label={t('common:Additon')}
          />
        </div>
        <div>
          <CheckboxWithTitle
            reverse
            className={styles.checkbox}
            checked={formik.values.agreement}
            onChange={() =>
              formik.setFieldValue('agreement', !formik.values.agreement)
            }
          >
            <span
              dangerouslySetInnerHTML={{
                __html: t('common:tariffSupport.accept'),
              }}
            />
          </CheckboxWithTitle>
          {formik.touched.agreement && (
            <div className={styles.error}>{formik.errors.agreement}</div>
          )}
        </div>
        <div className={styles.buttonHolder}>
          <Button className={styles.button} onClick={formik.handleSubmit}>
            {t('common:Send')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
